import type { CombinedError } from "urql"
import { useMutation } from "urql"

import { UpdateProductMutationDocument } from "@/graphql/codegen/graphql"
import type {
  ProductMutationUpdateArgs,
  ProductUpdateFailure,
  ProductUpdateInput,
  ProductUpdateMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseUpdateProductReturn {
  update: (input: ProductUpdateInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export interface UseUpdateProductProps {
  errorMessage?: string
  successMessage?: string
  onSuccess: () => void
}

export const useUpdateProduct = ({
  errorMessage = "Failed to update product.",
  successMessage = "Product successfully updated.",
  onSuccess,
}: UseUpdateProductProps): UseUpdateProductReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<ProductUpdateMutation, ProductMutationUpdateArgs>(
    UpdateProductMutationDocument
  )

  const update = async (input: ProductUpdateInput) => {
    const result = await executeMutation({ input })
    const { data, error } = result
    const output = data?.product.update

    if (error?.message || output?.__typename === "ProductUpdateFailure") {
      notify({
        type: NotificationType.Error,
        message: error?.message ?? (output as ProductUpdateFailure).error.message ?? errorMessage,
      })
      return
    }

    notify({
      type: NotificationType.Success,
      message: successMessage,
    })
    onSuccess?.()
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
